/**
 * This class manages "selection" within a container or set of containers.  Selection is closely related to, but not
 * identical to focus.  When focus moves into a container child it becomes selected.  However, when focus leaves a
 * child it does not necessarily become deselected.  It only becomes deselected if focus moves into another container
 * child.  So for example, if focus moves to a text field within the toolstrip the currently selected document does not
 * become deselected.  The SelectionManager oversees the mutual exclusion of selection within the containers it manages.
 *
 * The selected state of a child is stored in an "isSelected" property.  The SelectionManager both watches and sets these
 * properties.  The SelectionManager is also responsible for ensuring that selected children are visible to the user.
 * It accomplishes this by walking up the containment hierarchy when a child becomes selected, calling "revealChild"
 * methods that perform container-specific actions such as expanding collapsed panels or bringing tabs forward.
 * 
 * @copyright Copyright 2015-2020 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/aspect",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/Stateful",
    "dijit/focus",
    "dijit/popup",
    "dijit/registry",
    "mw-log/Log",
    "MW/layout/HidingContainer",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent"
], function (declare, lang, aspect, dom, domAttr, domClass, Stateful, focus, popup, registry, Log,
             HidingContainer, LayoutChildProperty, LayoutContainerEvent) {

    var SELECTED_CHILD = "selectedChild";

    /**
     * @class
     */
    var SelectionManager = declare(Stateful, {

        _successionBlockCount: 0,
        _blockFocus: false,
        _blockRevealChild: false,
        _immediateUnblockFocus: false,

        // The current selected child is also set as _lastSelectedChild. This is useful for Ctrl + F6 navigation
        // when there is no child selected and we do not have any reference child to find the next or previous child.
        _lastSelectedChild: null,

        constructor: function () {
            this._childProperties = {}; // Hash containing child properties indexed by widget id
            this._containerConnections = {};
        },

        /**
         * Registers a container so that the SelectionManager will manage the selection of its children.  The container
         * is presumed to emit CHILD_ADDED and CHILD_REMOVED notifications.
         *
         * @param container The container whose children should be managed
         */
        registerContainer: function (container) {
            this._containerConnections[container.id] = [
                container.on(LayoutContainerEvent.CHILD_ADDED, lang.hitch(this, "addChild")),
                container.on(LayoutContainerEvent.CHILD_REMOVED, lang.hitch(this, "removeChild"))
            ];
        },

        unregisterContainer: function (container) {
            var connections = this._containerConnections[container.id];
            if (connections) {
                connections.forEach(function (connection) {
                    connection.remove();
                });
                delete this._containerConnections[container.id];
            }
        },

        addChild: function (widget, properties) {
            if (properties.get(LayoutChildProperty.IS_SELECTABLE) === false) {
                return;
            }
            if (this._childProperties[widget.id]) {
                return;
            }

            /**
             * Generate a warning message if the widget neither has a focus method nor a tabindex.
             * See {@link UIContainer#addPanel} and {@link UIContainer#addDocument} for more details.
             */
            // g1736780: Selection in Toolstrip is handled in a different way where we show the mnemonics and not really
            // call the focus method or directly give focus to its DOM node. The last clause ensures that this
            // warning is not shown for the toolstrip.
            if (!widget.focus && !(widget.domNode && domAttr.has(widget.domNode, "tabindex")) && !widget.dontSelectOnFocus) {
                Log.warn("The widget (id: "+ widget.id + ") has neither a focus() method nor a tabindex and therefore " +
                    "won't accept focus correctly.");
            }

            var that = this;
            this._childProperties[widget.id] = properties;
            var selectionResponse = function (name, oldValue, newValue) {
                var previousSelection, successor;
                if (newValue && that.selectedChild !== widget) {

                    previousSelection = that.selectedChild;

                    // Update the current selection
                    that._changeAttrValue(SELECTED_CHILD, widget);

                    that._lastSelectedChild = that.selectedChild;

                    // Deselect the previously selected child
                    if (previousSelection) {
                        that._setSelected(that._childProperties[previousSelection.id], false);
                    }

                    if (!that._blockRevealChild) {
                        that._revealChild(widget);
                    }

                    // Give the widget focus if it doesn't already have it.  The first clause short circuits
                    // in the case where the SelectionManager itself is responding to focus.  The second clause
                    // covers the case where some other code set selection in response to focus.  Generally
                    // other code should not do this but it's best to guard against it. The last clause is to check
                    // if it is okay to give focus to the widget.
                    if (!that._isRespondingToFocus && widget.domNode && focus.activeStack.indexOf(widget.domNode.id) < 0 && !that._blockFocus) {
                        if (widget.focus) {
                            widget.focus();
                        } else {
                            focus.focus(widget.domNode);
                        }
                    }
                    
                    // g2149206: Set the newly selected child as showing if applicable
                    var props = that._childProperties[widget.id];
                    if (that._shouldSetShowingOnSelection(props)) {
                        props.set(LayoutChildProperty.IS_SHOWING, true);
                    }
                } else if (!newValue && that.selectedChild === widget) {
                    if (that._successionBlockCount > 0) {
                        that._changeAttrValue(SELECTED_CHILD, null);
                    } else {
                        // Deselecting the current child.  Choose a successor
                        successor = that.getNextChild(widget, true);
                        if (successor && that._childProperties[successor.id]) {
                            that._setSelected(that._childProperties[successor.id], true);
                        }
                    }
                }
            };
            properties.selectionManagerSelectWatch = properties.watch(LayoutChildProperty.IS_SELECTED, selectionResponse);
            if (properties.get(LayoutChildProperty.IS_SELECTED)) {
                selectionResponse(LayoutChildProperty.IS_SELECTED, false, true);
            }
            // Deselect a child when it is hidden but defer so that the new selection can take effect first.  Otherwise,
            // the selection succession code above will cause unwanted gyrations.
            properties.selectionManagerShowWatch = properties.watch(LayoutChildProperty.IS_SHOWING, function (name, oldValue, newValue) {
                if (!newValue && !that.shouldBlockSuccession()) {
                    setTimeout(function() {
                        if (!properties.get(LayoutChildProperty.IS_SHOWING)) {
                            that._setSelected(properties, false);
                        }
                    }, 0)
                }
            });

            // Select in response to focus
            var focusResponse = function () {
                that._isRespondingToFocus = true;
                that._setSelected(properties, true);
                that._isRespondingToFocus = false;
            };
            if (!widget.dontSelectOnFocus) {
                if (widget.onFocus) {
                    properties.selectionManagerOnFocusAspect = aspect.after(widget, 'onFocus', focusResponse);
                } else {
                    widget.onFocus = focusResponse;
                }
            }
        },
        
        _shouldSetShowingOnSelection: function (props) {
            return props &&
                (
                    (
                        props.hasOwnProperty(LayoutChildProperty.IS_COLLAPSED) &&
                        !props.get(LayoutChildProperty.IS_COLLAPSED)
                    ) ||
                    !props.hasOwnProperty(LayoutChildProperty.IS_COLLAPSED)
                ) &&
                props.hasOwnProperty(LayoutChildProperty.IS_SHOWING);
        },

        // Should be called before child removed from layout to allow selection succession based on current reference
        removeChild: function (widget, properties) {
            var successor;
            delete this._childProperties[widget.id];
            if (properties.selectionManagerSelectWatch) {
                properties.selectionManagerSelectWatch.unwatch();
                delete properties.selectionManagerSelectWatch;
            }
            if (properties.selectionManagerShowWatch) {
                properties.selectionManagerShowWatch.unwatch();
                delete properties.selectionManagerShowWatch;
            }
            if (properties.selectionManagerOnFocusAspect) {
                properties.selectionManagerOnFocusAspect.remove();
                delete properties.selectionManagerOnFocusAspect;
            }
            if (widget === this.selectedChild) {
                // Select successor
                if (properties.documentType) {
                    this._changeAttrValue(SELECTED_CHILD, null);
                    // For documents leave successor selection to document container and UIContainer
                } else {
                    successor = this._successionBlockCount > 0 ? null : this.getNextChild(widget, true);
                    if (successor) {
                        this._setSelected(this._childProperties[successor.id], true);
                    } else {
                        this._setSelected(properties, false);
                        this._changeAttrValue(SELECTED_CHILD, null);
                    }
                }
            }
        },

        setSuccessionFilter: function (canSelectFunction) {
            this._canSelectFilter = canSelectFunction;
        },

        blockSuccession: function () {
            this._successionBlockCount++;
        },

        unblockSuccession: function () {
            if (this._successionBlockCount > 0) {
                this._successionBlockCount--;
            }
        },

        shouldBlockSuccession: function () {
            return this._successionBlockCount > 0 ? true : false;
        },

        blockFocus: function () {
            this._blockFocus = true;
        },

        unblockFocus: function () {
            this._blockFocus = false;
        },

        shouldBlockFocus: function () {
            return this._blockFocus;
        },

        blockRevealChild: function () {
            this._blockRevealChild = true;
        },

        unblockRevealChild: function () {
            this._blockRevealChild = false;
        },

        shouldBlockRevealChild: function () {
            return this._blockRevealChild;
        },

        getLastSelectedChild: function () {
            return this._lastSelectedChild;
        },

        /**
         * Selects or deselects a widget.
         * @param {Object} widget - widget to be selected or deselected
         * @param {Boolean} value - true for selecting and false for deselecting
         * @param {Boolean} blockReaction - While selecting a widget, setting this parameter to true will prevent
         * giving focus to the widget's content and while deselecting a widget, setting this parameter to true will
         * prevent selecting the successive child.
         */
        setSelected: function (widget, value, blockReaction) {
            var properties =  this._childProperties[widget.id];
            if (properties) {
                if (blockReaction) {
                    if (value) {
                        this.blockFocus();
                    } else {
                        this.blockSuccession();
                    }
                }
                this._setSelected(properties, value);
                if (blockReaction) {
                    if (value) {
                        this.unblockFocus();
                    } else {
                        this.unblockSuccession();
                    }
                }
            }
        },

        _setSelected: function (properties, value) {
            if (value != properties.get(LayoutChildProperty.IS_SELECTED)) {
                properties.set(LayoutChildProperty.IS_SELECTED, value);
                if (value) {
                    if (properties.widget) {
                        this._lastSelectedChild = properties.widget;
                    } else {
                        this._lastSelectedChild = properties;
                    }
                }
            }
        },

        _selectedChildSetter: function(widget) {
            this.setSelected(widget, true)
        },

        _revealChild: function (widget) {
            var child  = widget;
            var parent = child.domNode ? child.getParent() : null;
            while (parent) {
                if (parent.revealChild) {
                    parent.revealChild(child);
                }
                child  = parent;
                parent = parent.getParent();
            }
        },

        _getFirstChild: function (widget, showing, forwardNavigationKeyPressed) {
            return (!widget || !widget.nextChild) ? widget : this._getFirstChild(widget.nextChild(null, showing, forwardNavigationKeyPressed), showing, forwardNavigationKeyPressed);
        },

        /**
         * Returns the next child.
         * @param {Object} widget - reference child
         * @param {Boolean} showing - if true then return a child that is already showing
         * @param {Boolean} forwardNavigationKeyPressed - true if this function is called from UIContainer when
         * forward navigation key is pressed
         * @returns {Object} result
         */
        getNextChild: function (widget, showing, forwardNavigationKeyPressed) {
            var parent = widget.getParent() || widget.parentBar;   // parentBar relates to case of panel expanded on-top
                                                                   // See BorderBar and geck 1458315.

            var next, result, widgetNotInChildProperties;
            if (!parent) {
                return null;
            }
            if (parent.nextChild) {
                if (!showing || (showing && HidingContainer.isShowingGlobally(parent))) {
                    while (true) {
                        next = parent.nextChild(widget, showing, forwardNavigationKeyPressed);
                        if (next) {
                            result = this._getFirstChild(next, showing, forwardNavigationKeyPressed);
                        }
                        else {
                            break;
                        }

                        if (result) {
                            if (this._childProperties[result.id]) {
                                // Check if the child is not already selected. A use case for this is when there is only one
                                // child of the container and the parameter "showing" is true, the same child keeps getting selected.
                                if (result.properties && result.properties.get(LayoutChildProperty.IS_SELECTED) === true) {
                                    break;
                                }
                                else {
                                    // If the succession filter prevents selection of "result", keep looking
                                    if (this._canSelectFilter && !this._canSelectFilter(result, this._childProperties[result.id])) {
                                        break;
                                    } else {
                                        return result;
                                    }
                                }
                            }
                            else {
                                // g1652550: When we come across any widget that is not in the SelectionManager's childProperties
                                // for the first time (when the parent is the top level borderContainer and we cannot make any more
                                // recursive calls), we save it for reference and when we come across the same widget again we
                                // break the while loop by returning null. This way we ensure that the while loop does not run forever.
                                if (widgetNotInChildProperties && widgetNotInChildProperties === result) {
                                    return null;
                                }

                                if (widgetNotInChildProperties === undefined && parent.getParent && parent.getParent() === null) {
                                    widgetNotInChildProperties = result;
                                }

                                // Set the correct reference child.
                                widget = result;
                                if (result.getParent && result.getParent() !== parent) {
                                    parent = result.getParent();
                                }
                            }
                        }
                        else {
                            break;
                        }
                    }
                }
            }
            return this.getNextChild(parent, showing, forwardNavigationKeyPressed);
        },

        _getLastChild: function (widget, showing, backwardNavigationKeyPressed) {
            return (!widget || !widget.previousChild) ? widget : this._getLastChild(widget.previousChild(null, showing,
                backwardNavigationKeyPressed), showing, backwardNavigationKeyPressed);
        },

        /**
         * Returns the previous child.
         * @param {Object} widget - reference child
         * @param {Boolean} showing - if true then return a child that is already showing
         * @param {Boolean} backwardNavigationKeyPressed - true if this function is called from UIContainer when
         * backward navigation key is pressed
         * @returns {Object} result
         */
        getPreviousChild: function (widget, showing, backwardNavigationKeyPressed) {
            var parent = widget.getParent() || widget.parentBar;

            var previous, result, widgetNotInChildProperties;
            if (!parent) {
                return null;
            }

            if (parent.previousChild) {
                if (!showing || (showing && HidingContainer.isShowingGlobally(parent))) {
                    while (true) {
                        previous = parent.previousChild(widget, showing, backwardNavigationKeyPressed);
                        if (previous) {
                            result = this._getLastChild(previous, showing, backwardNavigationKeyPressed);
                        }
                        else {
                            break;
                        }

                        if (result) {
                            if (this._childProperties[result.id]) {
                                // Check if the child is not already selected. A use case for this is when there is only one
                                // child of the container and the parameter "showing" is true, the same child keeps getting selected.
                                if (result.properties && result.properties.get(LayoutChildProperty.IS_SELECTED) === true) {
                                    break;
                                }
                                else {
                                    // If the succession filter prevents selection of "result", keep looking
                                    if (this._canSelectFilter && !this._canSelectFilter(result, this._childProperties[result.id])) {
                                        break;
                                    }
                                    else {
                                        return result;
                                    }
                                }
                            }
                            else {
                                // g1652550
                                if (widgetNotInChildProperties && widgetNotInChildProperties === result) {
                                    return null;
                                }

                                if (widgetNotInChildProperties === undefined && parent.getParent && parent.getParent() === null) {
                                    widgetNotInChildProperties = result;
                                }

                                // Set the correct reference child.
                                widget = result;
                                if (result.getParent && result.getParent() !== parent) {
                                    parent = result.getParent();
                                }
                            }
                        }
                        else {
                            break;
                        }
                    }
                }
            }
            return this.getPreviousChild(parent, showing, backwardNavigationKeyPressed);
        }
    });

    SelectionManager.SELECTED_CHILD = SELECTED_CHILD;

    return SelectionManager;
});